import React, { useState, useRef } from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
	Menu,
	MenuItem,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItemButton,
	ListItemText,
	CircularProgress,
	Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import './styles.css';
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../../../firebase';

const DirectoryCard = ({
	directory,
	onDelete,
	inAddMode = false,
	onRename,
	onNameChange,
	onSubmit,
	onCancel,
	onMove,
	onShare,
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [isRenaming, setIsRenaming] = useState(false);
	const [newName, setNewName] = useState(directory?.name || '');
	const [moveDialogOpen, setMoveDialogOpen] = useState(false);
	const [organizations, setOrganizations] = useState([]);
	const [orgsLoading, setOrgsLoading] = useState(false);
	const [selectedOrg, setSelectedOrg] = useState(null);
	const open = Boolean(anchorEl);
	const addInputRef = useRef(null);
	const menuJustClosed = useRef(false);

	const navigate = useNavigate();
	const { organizationId } = useParams();

	const handleDirectoryClick = () => {
		if (inAddMode || isRenaming || menuJustClosed.current) return;
		navigate(`/admin/organizations/${organizationId}/directories/${directory.id}/projects`);
	};

	const handleMenuOpen = (e) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleMenuClose = () => {
		menuJustClosed.current = true;
		setAnchorEl(null);
		setTimeout(() => { menuJustClosed.current = false; }, 0);
	};

	const handleAddKeyDown = (e) => {
		if (e.key === 'Enter') onSubmit?.();
		if (e.key === 'Escape') onCancel?.();
	};

	const handleRenameSubmit = () => {
		if (newName.trim()) {
			onRename?.(directory, newName.trim());
		}
		setIsRenaming(false);
	};

	const handleRenameKeyDown = (e) => {
		if (e.key === 'Enter') handleRenameSubmit();
		if (e.key === 'Escape') setIsRenaming(false);
	};

	const handleOpenMoveDialog = async () => {
		setMoveDialogOpen(true);
		setSelectedOrg(null);
		setOrgsLoading(true);
		try {
			const snapshot = await getDocs(collection(db, 'organizations'));
			const orgs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
			// Exclude the current organization
			setOrganizations(orgs.filter((o) => o.id !== organizationId));
		} finally {
			setOrgsLoading(false);
		}
	};

	const handleMoveConfirm = () => {
		if (selectedOrg) {
			onMove?.(directory, selectedOrg);
		}
		setMoveDialogOpen(false);
	};

	return (
		<div
			className={`dir-card${inAddMode ? ' dir-card--add-mode' : ''}`}
			onClick={handleDirectoryClick}
		>
			<div className="dir-card__icon">
				<FolderIcon />
			</div>

			<div className="dir-card__body">
				{inAddMode ? (
					<>
						<input
							ref={addInputRef}
							className="dir-card__name-input"
							autoFocus
							placeholder="Project name"
							value={directory.name}
							onChange={(e) => onNameChange?.(e.target.value)}
							onKeyDown={handleAddKeyDown}
							onClick={(e) => e.stopPropagation()}
						/>
						<div className="dir-card__add-actions" onClick={(e) => e.stopPropagation()}>
							<button className="dir-card__add-confirm" onClick={onSubmit}>Add</button>
							<button className="dir-card__add-cancel" onClick={onCancel}>Cancel</button>
						</div>
					</>
				) : isRenaming ? (
					<div className="dir-card__rename-row" onClick={(e) => e.stopPropagation()}>
						<input
							className="dir-card__name-input"
							autoFocus
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							onKeyDown={handleRenameKeyDown}
						/>
						<div className="dir-card__add-actions">
							<button className="dir-card__add-confirm" onClick={handleRenameSubmit}>Save</button>
							<button className="dir-card__add-cancel" onClick={() => setIsRenaming(false)}>Cancel</button>
						</div>
					</div>
				) : (
					<span className="dir-card__name">{directory.name}</span>
				)}
			</div>

			{!inAddMode && !isRenaming && (
				<div className="dir-card__right">
					<ArrowForwardIcon className="dir-card__arrow" fontSize="small" />
					<IconButton
						className="dir-card__menu-btn"
						size="small"
						onClick={handleMenuOpen}
						aria-label="Project options"
					>
						<MoreVertIcon fontSize="small" />
					</IconButton>
					<Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
						<MenuItem
							onClick={(e) => {
								e.stopPropagation();
								onShare?.(directory);
								handleMenuClose();
							}}
						>
							Share
						</MenuItem>
						<MenuItem
							onClick={(e) => {
								e.stopPropagation();
								handleMenuClose();
								handleOpenMoveDialog();
							}}
						>
							Move
						</MenuItem>
						<MenuItem
							onClick={(e) => {
								e.stopPropagation();
								setIsRenaming(true);
								setNewName(directory.name);
								handleMenuClose();
							}}
						>
							Rename
						</MenuItem>
						<MenuItem
							onClick={(e) => {
								e.stopPropagation();
								onDelete?.(directory);
								handleMenuClose();
							}}
							sx={{ color: '#c62828' }}
						>
							Delete
						</MenuItem>
					</Menu>
				</div>
			)}

			{/* Move Dialog */}
			<Dialog
				open={moveDialogOpen}
				onClose={() => setMoveDialogOpen(false)}
				onClick={(e) => e.stopPropagation()}
				PaperProps={{ sx: { borderRadius: '12px', minWidth: 340 } }}
			>
				<DialogTitle sx={{ fontFamily: '"Open Sans", sans-serif', fontWeight: 700, color: '#0f2d4e' }}>
					Move "{directory?.name}" to…
				</DialogTitle>
				<DialogContent dividers sx={{ p: 0 }}>
					{orgsLoading ? (
						<div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
							<CircularProgress size={28} sx={{ color: '#0f2d4e' }} />
						</div>
					) : organizations.length === 0 ? (
						<Typography sx={{ p: 3, color: 'text.secondary', fontFamily: '"Open Sans", sans-serif' }}>
							No other organizations available.
						</Typography>
					) : (
						<List disablePadding>
							{organizations.map((org) => (
								<ListItemButton
									key={org.id}
									selected={selectedOrg?.id === org.id}
									onClick={() => setSelectedOrg(org)}
									sx={{
										'&.Mui-selected': { background: 'rgba(15,45,78,0.08)' },
										'&.Mui-selected:hover': { background: 'rgba(15,45,78,0.12)' },
									}}
								>
									<ListItemText
										primary={org.name}
										primaryTypographyProps={{ fontFamily: '"Open Sans", sans-serif', fontWeight: 600, color: '#0f2d4e' }}
									/>
								</ListItemButton>
							))}
						</List>
					)}
				</DialogContent>
				<DialogActions sx={{ p: 2, gap: 1 }}>
					<Button
						onClick={() => setMoveDialogOpen(false)}
						sx={{ fontFamily: '"Open Sans", sans-serif', textTransform: 'none', color: '#0f2d4e' }}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={!selectedOrg}
						onClick={handleMoveConfirm}
						sx={{
							background: '#0f2d4e',
							fontFamily: '"Open Sans", sans-serif',
							fontWeight: 600,
							textTransform: 'none',
							borderRadius: '8px',
							'&:hover': { background: '#1a4a7a' },
							'&.Mui-disabled': { background: 'rgba(15,45,78,0.2)', color: '#fff' },
						}}
					>
						Move
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default DirectoryCard;
